import { Ref } from 'vue'
import { SingleSelect } from '@/components/Fields/dataObjects/SingleSelect'
import { MultiSelect } from '@/components/Fields/dataObjects/MultiSelect'
import { FreeText } from '@/components/Fields/dataObjects/FreeText'
import { FileUpload } from '@/components/Fields/dataObjects/FileUpload'
import { DateField } from '@/components/Fields/dataObjects/DateField'
import { BooleanField } from '@/components/Fields/dataObjects/BooleanField'
import {
  CustomFieldDetailFragment,
  CustomFieldListFragment,
  CustomFieldSettings,
  CustomFieldType,
  CustomFieldValidationRules,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import { CustomFieldOption } from '@/components/Fields/dataObjects/CustomFieldOption'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

export const CustomFieldTypeClasses = {
  [CustomFieldType.SingleSelect]: SingleSelect,
  [CustomFieldType.MultiSelect]: MultiSelect,
  [CustomFieldType.FreeText]: FreeText,
  [CustomFieldType.FileUpload]: FileUpload,
  [CustomFieldType.Date]: DateField,
  [CustomFieldType.Boolean]: BooleanField,
}

export const CustomFieldTypeGqlToInput = {
  [CustomFieldType.SingleSelect]: 'singleSelect',
  [CustomFieldType.MultiSelect]: 'multiSelect',
  [CustomFieldType.FreeText]: 'freeText',
  [CustomFieldType.FileUpload]: 'fileUpload',
  [CustomFieldType.Date]: 'date',
  [CustomFieldType.Boolean]: 'boolean',
}

export function newInstanceOfCustomField(
  field:
    | (Pick<CustomFieldDetailFragment, 'appliesTo'> & Partial<CustomFieldDetailFragment>)
    | (Pick<CustomFieldListFragment, 'appliesTo'> & Partial<CustomFieldListFragment>),
  fieldType: CustomFieldType = CustomFieldType.FreeText,
): CustomField {
  return new CustomFieldTypeClasses[fieldType](field)
}

export function extractFieldsGqlVariables(field: Ref<CustomField>) {
  return {
    fieldType: field.value.fieldType,
    title: field.value.title!,
    slug: field.value.slug,
    visibility: field.value.visibility,
    approval: field.value.approval,
    apiOnly: field.value.apiOnly,
    workerInputAllowed: field.value.workerInputAllowed,
    workerInputStage: field.value.workerInputStage,
    recruiterInputAllowed: field.value.recruiterInputAllowed,
    recruiterInputStage: field.value.recruiterInputStage,
    showOnInvoice: field.value.showOnInvoice,
    sharedWithClients: field.value.sharedWithClients,
    sharedWithStaffingAgencies: field.value.sharedWithStaffingAgencies,
    description: field.value.description,
    settings: field.value.settings.toGraphQLInput(),
    options: field.value.optionsForGraphQL(),
  }
}

export function isCustomFieldRequired(customFieldSettings: CustomFieldSettings): boolean {
  return (
    customFieldSettings.validation.find(
      (rule: CustomFieldValidationRules) => rule === CustomFieldValidationRules.Required,
    ) !== undefined
  )
}

export function getCustomFieldCautionText(appliesTo: TypeSupportingCustomFieldValues): string {
  switch (appliesTo) {
    case TypeSupportingCustomFieldValues.Job:
      return $t('This field is already used by at least one job, edit with caution.')
    case TypeSupportingCustomFieldValues.TrustedContact:
      return $t('This field is already used by at least one trusted contact, edit with caution.')
    case TypeSupportingCustomFieldValues.Contract:
      return $t('This field is already used by at least one contract, edit with caution.')
    case TypeSupportingCustomFieldValues.PaymentRequest:
      return $t('This field is already used by at least one payment request, edit with caution.')
    case TypeSupportingCustomFieldValues.CompanyRecruiter:
      return $t('This field is already used by at least one staffing agency, edit with caution.')
    case TypeSupportingCustomFieldValues.Expense:
      return $t('This field is already used by at least one expense, edit with caution.')
    case TypeSupportingCustomFieldValues.Hire:
      return $t('This field is already used by at least one hire, edit with caution.')
    default:
      return $t('This field is already in use. Edit with caution.')
  }
}

export function getFieldTypeLabel(fieldType: CustomFieldType): string {
  switch (fieldType) {
    case CustomFieldType.FreeText:
      return $t('Text')
    case CustomFieldType.SingleSelect:
      return $t('Dropdown')
    case CustomFieldType.MultiSelect:
      return $t('Multi-select dropdown')
    case CustomFieldType.FileUpload:
      return $t('File upload')
    case CustomFieldType.Date:
      return $t('Date')
    case CustomFieldType.Boolean:
      return $t('Boolean')
    default:
      return fieldType
  }
}

export function getAppliesToText(appliesTo: TypeSupportingCustomFieldValues): string {
  switch (appliesTo) {
    case TypeSupportingCustomFieldValues.Contract:
      return $t('Contract')
    case TypeSupportingCustomFieldValues.TrustedContact:
      return $t('Trusted Contact')
    case TypeSupportingCustomFieldValues.Job:
      return $t('Job')
    case TypeSupportingCustomFieldValues.PaymentRequest:
      return $t('Payment Request')
    case TypeSupportingCustomFieldValues.CompanyRecruiter:
      return $t('Staffing Agency')
    case TypeSupportingCustomFieldValues.Expense:
      return $t('Expense')
    case TypeSupportingCustomFieldValues.Hire:
      return $t('Hire')
    default:
      return ''
  }
}

/**
 * Puts back saved options missing from the form, each at its saved position.
 *
 * Used after the server refuses to remove options rate cards key on: the whole
 * save was rolled back, so every saved option still exists, while the form's
 * other edits are left as they are. A value retyped as a new option is replaced
 * by its saved original rather than listed twice.
 */
export function restoreSavedOptions(
  current: CustomFieldOption[],
  saved: CustomFieldOption[],
): CustomFieldOption[] {
  const currentIds = new Set(current.map((option) => option.id).filter((id) => id !== null))
  const restored = [...current]

  saved.forEach((option, index) => {
    if (option.id === null || currentIds.has(option.id)) {
      return
    }

    const retyped = restored.findIndex(
      (candidate) => candidate.id === null && candidate.value === option.value,
    )
    if (retyped !== -1) {
      restored.splice(retyped, 1, option)

      return
    }

    restored.splice(Math.min(index, restored.length), 0, option)
  })

  return restored
}
